import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useStore } from '../store/useStore'
import { api } from '../lib/api'
import ChatMessageList from '../components/chat/ChatMessageList'
import ChatInput from '../components/chat/ChatInput'
import MinigameOverlay from '../components/chat/MinigameOverlay'
// PointConfirmModal 제거 - 미니게임 무료화
import StageUpgradeModal from '../components/StageUpgradeModal'
import AlertModal from '../components/AlertModal'
import {
  useChatMessages,
  useChatSuggestions,
  useMinigame,
  useQuestionReveal,
  useChatTranslation,
} from '../hooks'

export default function ChatPage() {
  const { matchId } = useParams()
  const { user, points, fetchPoints } = useStore()

  // Alert modal state
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')

  // Stage upgrade modal state
  const [showStageUpgradeModal, setShowStageUpgradeModal] = useState(false)
  const [stageUpgradeInfo, setStageUpgradeInfo] = useState<{
    currentStage: number
    nextStage: number
    nextStageCost: number
    stageName: string
    message: string
  } | null>(null)

  const showAlert = (msg: string) => {
    setAlertMessage(msg)
    setAlertOpen(true)
  }

  // Chat messages hook
  const {
    messages,
    setMessages,
    matchInfo,
    loading,
    sending,
    sendingMessage,
    isOtherUserTyping,
    input,
    setInput,
    handleInputChange,
    handleSend: baseSend,
    fetchMessagesQuietly,
  } = useChatMessages({
    matchId,
    userId: user?.id,
    onStageLockedError: (error) => {
      setStageUpgradeInfo({
        currentStage: error.current_stage || 1,
        nextStage: error.next_stage || 2,
        nextStageCost: error.next_stage_cost || 0,
        stageName: matchInfo?.stage?.stage_name || 'Current Stage',
        message: error.message || '스테이지 한도에 도달했습니다.',
      })
      setShowStageUpgradeModal(true)
    },
    onMessageBlockedError: (error) => {
      const detailedMessage = `${error.message}\n\n이유: ${error.details || error.reason}`
      showAlert(detailedMessage)
    },
  })

  // Wrap handleSend to show alert on generic error
  const handleSend = async () => {
    try {
      await baseSend()
    } catch {
      showAlert('메시지 전송에 실패했습니다.')
    }
  }

  // Suggestions hook
  const {
    suggestions,
    loadingSuggestions,
    showSuggestions,
    coachTip,
    suggestionError,
    setShowSuggestions,
    handleGetSuggestions,
    handleSelectSuggestion,
  } = useChatSuggestions(matchId, setInput)

  // Translation hook (무료화 - 모달 없음)
  const {
    showTranslation,
    handleTranslate,
  } = useChatTranslation(messages, setMessages, user?.locale || 'ko')

  // Minigame hook (무료화 - 모달 없이 바로 시작)
  const {
    minigameActive,
    minigameSession,
    minigameTimeLeft,
    handleStartMinigame,
    handleMinigameAnswer,
    dismissMinigame,
  } = useMinigame(matchId, fetchMessagesQuietly, showAlert)

  // Question reveal hook
  const {
    availableQuestionsCount,
    revealingQuestion,
    showGiftAnimation,
    handleRevealQuestion,
  } = useQuestionReveal(
    matchId,
    messages.length,
    matchInfo?.is_ai_chatting || false,
    showAlert,
    fetchMessagesQuietly
  )

  // Stage upgrade handler
  const confirmStageUpgrade = async () => {
    setShowStageUpgradeModal(false)
    if (!stageUpgradeInfo || !matchId) return

    try {
      const result = await api.matches.upgradeStage(parseInt(matchId))

      if (result.upgraded) {
        showAlert(`${result.message}`)
        await fetchMessagesQuietly()
        await fetchPoints()
      } else {
        showAlert(result.message)
      }
    } catch (error: any) {
      showAlert(error.message || '스테이지 업그레이드에 실패했습니다.')
    }
  }

  // Stage info
  const stageProgress = matchInfo?.stage?.message_limit
    ? (matchInfo.stage.message_count / matchInfo.stage.message_limit) * 100
    : 0

  const getStageColor = () => {
    if (!matchInfo?.stage) return 'primary'
    switch (matchInfo.stage.current_stage) {
      case 1: return 'imagine'
      case 2: return 'hear'
      case 3: return 'see'
      default: return 'primary'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-gradient-to-b from-base-100 to-base-200/50">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-primary" />
          <p className="text-sm text-base-content/50 mt-4">채팅을 불러오는 중...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[100dvh] flex flex-col bg-gradient-to-b from-base-100 to-base-200/30 overflow-hidden">
      {/* Gift Animation */}
      {showGiftAnimation && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
          <div className="text-8xl animate-bounce">🎁</div>
        </div>
      )}

      {/* Header */}
      <header className="nav-glass sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Back Button */}
            <Link to="/matches" className="btn btn-ghost btn-circle btn-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>

            {/* Other User Info */}
            <Link to={matchInfo?.other_user?.id ? `/profile/${matchInfo.other_user.id}` : '#'} className="flex items-center gap-3 flex-1 min-w-0">
              <div className="avatar-premium">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-coral-200 to-mint-200">
                  {matchInfo?.other_user?.picture ? (
                    <img src={matchInfo.other_user.picture} alt={matchInfo.other_user.username} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold">
                      {matchInfo?.other_user?.username?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-base-content truncate">
                  {matchInfo?.other_user?.username}
                </h2>
                <div className="flex items-center gap-2">
                  {matchInfo?.is_ai_chatting && (
                    <span className="text-xs text-secondary flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                      AI 응답 중
                    </span>
                  )}
                  {matchInfo?.stage && (
                    <span className="text-xs text-base-content/50">
                      Stage {matchInfo.stage.current_stage}
                    </span>
                  )}
                </div>
              </div>
            </Link>

            {/* Points */}
            <div className="points-badge text-xs py-1 px-2">
              <span>🪙</span>
              <span className="text-xs">{points?.general_points || 0}</span>
            </div>
          </div>
        </div>

        {/* Stage Progress Bar */}
        {matchInfo?.stage?.message_limit && (
          <div className="px-4 pb-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={`font-medium text-${getStageColor()}-600`}>
                {matchInfo.stage.stage_name}
              </span>
              <span className="text-base-content/50">
                {matchInfo.stage.message_count}/{matchInfo.stage.message_limit}
              </span>
            </div>
            <div className="h-1.5 bg-base-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${stageProgress > 90 ? 'bg-error' :
                  stageProgress > 70 ? 'bg-warning' :
                    `bg-gradient-to-r from-${getStageColor()}-400 to-${getStageColor()}-600`
                  }`}
                style={{ width: `${Math.min(stageProgress, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* AI Chatting Banner */}
        {matchInfo?.is_ai_chatting && (
          <div className="px-4 pb-2">
            <div className="bg-secondary/10 rounded-xl px-4 py-2 flex items-center justify-between">
              <span className="text-sm text-secondary flex items-center gap-2">
                <span>🤖</span>
                AI가 대신 대화하고 있어요
              </span>
              <button className="btn btn-xs btn-secondary rounded-full">
                직접 대화하기
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Messages */}
      <ChatMessageList
        messages={messages}
        currentUserId={user?.id}
        showTranslation={showTranslation}
        onTranslate={handleTranslate}
        isOtherUserTyping={isOtherUserTyping}
        sending={sending}
        sendingMessage={sendingMessage}
        userLocale={user?.locale || 'ko'}
        currentStage={matchInfo?.stage?.current_stage || 1}
      />

      {/* Minigame Overlay */}
      {minigameActive && minigameSession && (
        <MinigameOverlay
          session={minigameSession}
          timeLeft={minigameTimeLeft}
          onAnswer={handleMinigameAnswer}
          onClose={dismissMinigame}
        />
      )}

      {/* Input & Suggestions Container - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-40">
        {/* Suggestions - Coach Style (above input) */}
        {showSuggestions && (
          <div className="bg-white border-t border-primary/20 p-3 shadow-lg max-h-[50vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">💝</span>
                <span className="font-medium text-primary text-sm">연애 코치</span>
              </div>
              <button
                onClick={() => setShowSuggestions(false)}
                className="btn btn-ghost btn-xs btn-circle"
              >
                ✕
              </button>
            </div>

            {/* Coach Tip */}
            {coachTip && (
              <div className="bg-primary/10 rounded-lg p-2 mb-2 text-xs text-primary-content/80">
                💡 {coachTip}
              </div>
            )}

            {loadingSuggestions ? (
              <div className="flex items-center justify-center py-6">
                <span className="loading loading-spinner loading-md text-primary"></span>
                <span className="ml-2 text-sm text-base-content/60">코치가 생각 중...</span>
              </div>
            ) : suggestions.length > 0 ? (
              <div className="space-y-2">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectSuggestion(suggestion.text)}
                    className="w-full text-left p-2.5 bg-white hover:bg-primary/5 rounded-xl border border-base-200 hover:border-primary/30 transition-all group"
                  >
                    <div className="font-medium text-sm text-base-content group-hover:text-primary">
                      "{suggestion.text}"
                    </div>
                    {suggestion.reason && (
                      <div className="text-xs text-base-content/50 mt-1">
                        → {suggestion.reason}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : suggestionError ? (
              <div className="text-sm text-center text-error py-3 bg-error/10 rounded-lg">
                {suggestionError}
              </div>
            ) : (
              <div className="text-sm text-center text-base-content/60 py-3 bg-white/50 rounded-lg">
                {coachTip || '상대방의 답변을 기다리고 있어요 😊'}
              </div>
            )}

            <div className="text-xs text-center mt-2 text-base-content/40">
              💝 무료 연애 코치 (오늘 5회)
            </div>
          </div>
        )}

        {/* Input */}
        <div className="chat-input-container">
          <ChatInput
            input={input}
            onInputChange={handleInputChange}
            onSend={handleSend}
            onGetSuggestions={handleGetSuggestions}
            sending={sending}
            loadingSuggestions={loadingSuggestions}
            stageLocked={false}
            stageLockedMessage=""
            onStartMinigame={handleStartMinigame}
            minigameActive={minigameActive}
            onRevealQuestion={handleRevealQuestion}
            availableQuestionsCount={availableQuestionsCount}
            revealingQuestion={revealingQuestion}
          />
        </div>
      </div>

      {/* Modals */}
      {/* 번역 모달 제거 - 무료화 */}
      {/* 미니게임 모달 제거 - 무료화 */}

      {stageUpgradeInfo && (
        <StageUpgradeModal
          isOpen={showStageUpgradeModal}
          onClose={() => {
            setShowStageUpgradeModal(false)
            setStageUpgradeInfo(null)
          }}
          onConfirm={confirmStageUpgrade}
          currentStage={stageUpgradeInfo.currentStage}
          nextStage={stageUpgradeInfo.nextStage}
          nextStageCost={stageUpgradeInfo.nextStageCost}
          currentPoints={points?.general_points || 0}
          message={stageUpgradeInfo.message}
        />
      )}

      <AlertModal
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
      />
    </div>
  )
}
